import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLocale } from '../context/LocaleContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  ArrowLeft, 
  Droplets, 
  Moon, 
  Utensils, 
  Dumbbell, 
  DollarSign, 
  CheckCircle2, 
  ChevronRight,
  Sparkles,
  Target,
  TrendingUp,
  Clock,
  MessageCircle
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * 📋 Suggested Feed - AI-Curated Personalized Suggestions
 * 
 * Shows actionable tips, nudges, and suggestions based on:
 * - User's health data (water, sleep, meals, exercise)
 * - Task completion status
 * - Emotional patterns
 * - Time of day
 */
const SuggestedFeed = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, locale } = useLocale();
  const { toast } = useToast();
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    loadSuggestions();
  }, []);

  const loadSuggestions = async () => {
    try {
      setLoading(true);
      
      // Fetch smart dashboard data for suggestions
      const dashboardResponse = await fetch(`${API_URL}/api/home/smart-dashboard`, {
        credentials: 'include'
      });
      
      let dashboardData = {};
      if (dashboardResponse.ok) {
        dashboardData = await dashboardResponse.json();
      }

      // Fetch forecasts for proactive suggestions
      const forecastResponse = await fetch(`${API_URL}/api/adaptive/forecasts`, {
        credentials: 'include'
      });
      
      let forecasts = [];
      if (forecastResponse.ok) {
        const forecastData = await forecastResponse.json();
        forecasts = forecastData.forecasts || [];
      }

      // Build suggestions list
      const suggestionsList = [];

      // Add daily suggestion if available
      if (dashboardData.daily_suggestion) {
        // Handle both string and object formats for daily_suggestion
        const suggestionText = typeof dashboardData.daily_suggestion === 'string' 
          ? dashboardData.daily_suggestion 
          : (dashboardData.daily_suggestion.text || dashboardData.daily_suggestion.body || '');
        const suggestionEmoji = typeof dashboardData.daily_suggestion === 'object' 
          ? (dashboardData.daily_suggestion.emoji || '✨')
          : '✨';
        
        if (suggestionText) {
          suggestionsList.push({
            id: 'daily',
            type: 'daily',
            icon: <Sparkles className="h-5 w-5" />,
            title: t('suggested.dailyTip') || 'Daily Tip',
            content: suggestionText,
            emoji: suggestionEmoji,
            action: typeof dashboardData.daily_suggestion === 'object' ? dashboardData.daily_suggestion.action : null,
            priority: 'high',
            color: 'purple'
          });
        }
      }

      // Check health metrics and add suggestions - handle nested data structure
      const healthProgress = dashboardData.dashboard?.progress || dashboardData.progress || dashboardData.health_progress || {};
      const waterGlasses = healthProgress.water_glasses || 0;
      const waterGoal = healthProgress.water_goal || 8;
      const waterPercentage = (waterGlasses / waterGoal) * 100;
      
      // Water suggestion
      if (waterPercentage < 50) {
        suggestionsList.push({
          id: 'water',
          type: 'health',
          icon: <Droplets className="h-5 w-5" />,
          title: t('suggested.stayHydrated') || 'Stay Hydrated',
          content: t('suggested.waterReminder') || `You've had ${waterGlasses} glasses today. Aim for ${waterGoal} glasses!`,
          emoji: '💧',
          actionUrl: '/health?tab=water',
          actionLabel: t('suggested.logWater') || 'Log Water',
          priority: 'medium',
          color: 'blue'
        });
      }

      // Sleep suggestion
      if (!dashboardData.sleep_logged) {
        suggestionsList.push({
          id: 'sleep',
          type: 'health',
          icon: <Moon className="h-5 w-5" />,
          title: t('suggested.trackSleep') || 'Track Your Sleep',
          content: t('suggested.sleepReminder') || "How did you sleep last night? Logging helps Allora understand your patterns.",
          emoji: '😴',
          actionUrl: '/health?tab=sleep',
          actionLabel: t('suggested.logSleep') || 'Log Sleep',
          priority: 'medium',
          color: 'indigo'
        });
      }

      // Meal suggestion (check time)
      const hour = new Date().getHours();
      const caloriesLogged = healthProgress.calories || 0;
      if ((hour >= 7 && hour <= 9) || (hour >= 12 && hour <= 14) || (hour >= 18 && hour <= 20)) {
        const mealLogged = caloriesLogged > 0;
        if (!mealLogged) {
          const mealType = hour < 11 ? 'breakfast' : hour < 15 ? 'lunch' : 'dinner';
          suggestionsList.push({
            id: 'meal',
            type: 'health',
            icon: <Utensils className="h-5 w-5" />,
            title: t(`suggested.log${mealType.charAt(0).toUpperCase() + mealType.slice(1)}`) || `Log ${mealType}`,
            content: t('suggested.mealReminder') || "Don't forget to log your meal for better nutrition tracking.",
            emoji: mealType === 'breakfast' ? '🥣' : mealType === 'lunch' ? '🥗' : '🍽️',
            actionUrl: '/health?tab=food',
            actionLabel: t('suggested.logMeal') || 'Log Meal',
            priority: 'low',
            color: 'orange'
          });
        }
      }

      // Exercise suggestion
      if (healthProgress.exercise !== undefined && healthProgress.exercise < 30) {
        suggestionsList.push({
          id: 'exercise',
          type: 'health',
          icon: <Dumbbell className="h-5 w-5" />,
          title: t('suggested.moveMore') || 'Time to Move',
          content: t('suggested.exerciseReminder') || 'A short walk or stretch can boost your energy and mood!',
          emoji: '🏃',
          actionUrl: '/health?tab=exercise',
          actionLabel: t('suggested.logWorkout') || 'Log Workout',
          priority: 'low',
          color: 'green'
        });
      }

      // Task suggestions - check nested data structure
      const progress = dashboardData.dashboard?.progress || dashboardData.progress || {};
      const pendingTasks = progress.tasks_pending || dashboardData.pending_tasks || 0;
      const billsDue = progress.bills_due || 0;
      
      if (pendingTasks > 0) {
        suggestionsList.push({
          id: 'tasks',
          type: 'productivity',
          icon: <Target className="h-5 w-5" />,
          title: t('suggested.pendingTasks') || 'Pending Tasks',
          content: t('suggested.tasksReminder', { count: pendingTasks }) || 
            `You have ${pendingTasks} task${pendingTasks > 1 ? 's' : ''} waiting. Focus on one at a time!`,
          emoji: '📋',
          actionUrl: '/work',
          actionLabel: t('suggested.viewTasks') || 'View Tasks',
          priority: 'medium',
          color: 'amber'
        });
      }

      // Bills due suggestion
      if (billsDue > 0) {
        suggestionsList.push({
          id: 'bills',
          type: 'finance',
          icon: <DollarSign className="h-5 w-5" />,
          title: t('suggested.billsDue') || 'Bills Due',
          content: `You have ${billsDue} bill${billsDue > 1 ? 's' : ''} due or overdue. Don't miss the deadline!`,
          emoji: '💰',
          actionUrl: '/life',
          actionLabel: t('suggested.viewBills') || 'View Bills',
          priority: 'high',
          color: 'red'
        });
      }

      // Add forecasts as suggestions
      forecasts.forEach((forecast, index) => {
        // Handle both string and object formats
        const forecastMessage = typeof forecast.message === 'string' 
          ? forecast.message 
          : (forecast.message?.body || forecast.message?.text || '');
        
        if (forecastMessage && index < 2) {
          suggestionsList.push({
            id: `forecast-${index}`,
            type: 'forecast',
            icon: <TrendingUp className="h-5 w-5" />,
            title: t('suggested.proactiveTip') || 'Proactive Tip',
            content: forecastMessage,
            emoji: forecast.emoji || '🔮',
            priority: 'low',
            color: 'teal'
          });
        }
      });

      // Set stats - handle nested data structure
      const tasksCompleted = progress.tasks_completed || dashboardData.completed_tasks || 0;
      setStats({
        waterProgress: waterPercentage || 0,
        caloriesProgress: caloriesLogged || 0,
        tasksCompleted: tasksCompleted,
        streak: dashboardData.dashboard?.streaks?.longest || dashboardData.streak || 0
      });

      setSuggestions(suggestionsList);
    } catch (error) {
      console.error('Error loading suggestions:', error);
      toast({
        title: t('error.title') || 'Error',
        description: t('error.loadingSuggestions') || 'Could not load suggestions',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSuggestionAction = (suggestion) => {
    if (suggestion.actionUrl) {
      navigate(suggestion.actionUrl);
    } else if (suggestion.action?.url) {
      navigate(suggestion.action.url);
    }
  };

  const getColorClasses = (color) => {
    const colors = {
      purple: 'from-purple-400 to-pink-500 bg-purple-50 border-purple-200 text-purple-700',
      blue: 'from-blue-400 to-cyan-500 bg-blue-50 border-blue-200 text-blue-700',
      indigo: 'from-indigo-400 to-purple-500 bg-indigo-50 border-indigo-200 text-indigo-700',
      orange: 'from-orange-400 to-amber-500 bg-orange-50 border-orange-200 text-orange-700',
      green: 'from-green-400 to-emerald-500 bg-green-50 border-green-200 text-green-700',
      amber: 'from-amber-400 to-yellow-500 bg-amber-50 border-amber-200 text-amber-700',
      teal: 'from-teal-400 to-cyan-500 bg-teal-50 border-teal-200 text-teal-700'
    };
    return colors[color] || colors.purple;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-teal-50 to-white flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-teal-400 to-cyan-500 flex items-center justify-center animate-pulse">
            <Sparkles className="h-8 w-8 text-white" />
          </div>
          <p className="text-teal-600 font-medium">{t('suggested.loading') || 'Loading your suggestions...'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-teal-50 via-cyan-50 to-white">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white/80 backdrop-blur-lg border-b border-teal-100 px-4 py-3">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          <button
            onClick={() => navigate('/')}
            className="p-2 hover:bg-teal-100 rounded-full transition-colors"
          >
            <ArrowLeft className="h-5 w-5 text-teal-600" />
          </button>
          <div className="flex-1">
            <h1 className="font-semibold text-teal-900">{t('suggested.title') || 'Suggested for You'}</h1>
            <p className="text-xs text-teal-500">{t('suggested.subtitle') || 'Personalized tips and actions'}</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/allora-chat')}
            className="text-teal-600 border-teal-200 hover:bg-teal-50"
          >
            <MessageCircle className="h-4 w-4 mr-1" />
            {t('suggested.talkToAllora') || 'Talk to Allora'}
          </Button>
        </div>
      </div>

      {/* Quick Stats */}
      {stats && (
        <div className="px-4 py-4">
          <div className="max-w-2xl mx-auto grid grid-cols-4 gap-2">
            <div className="bg-white rounded-xl p-3 text-center shadow-sm border border-blue-100">
              <Droplets className="h-5 w-5 text-blue-500 mx-auto mb-1" />
              <p className="text-lg font-semibold text-blue-600">{Math.round(stats.waterProgress)}%</p>
              <p className="text-xs text-gray-500">{t('suggested.water') || 'Water'}</p>
            </div>
            <div className="bg-white rounded-xl p-3 text-center shadow-sm border border-orange-100">
              <Utensils className="h-5 w-5 text-orange-500 mx-auto mb-1" />
              <p className="text-lg font-semibold text-orange-600">{Math.round(stats.caloriesProgress)}%</p>
              <p className="text-xs text-gray-500">{t('suggested.calories') || 'Calories'}</p>
            </div>
            <div className="bg-white rounded-xl p-3 text-center shadow-sm border border-green-100">
              <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto mb-1" />
              <p className="text-lg font-semibold text-green-600">{stats.tasksCompleted}</p>
              <p className="text-xs text-gray-500">{t('suggested.tasks') || 'Tasks'}</p>
            </div>
            <div className="bg-white rounded-xl p-3 text-center shadow-sm border border-amber-100">
              <TrendingUp className="h-5 w-5 text-amber-500 mx-auto mb-1" />
              <p className="text-lg font-semibold text-amber-600">{stats.streak}</p>
              <p className="text-xs text-gray-500">{t('suggested.streak') || 'Streak'}</p>
            </div>
          </div>
        </div>
      )}

      {/* Suggestions List */}
      <div className="px-4 pb-24">
        <div className="max-w-2xl mx-auto space-y-3">
          {suggestions.length === 0 ? (
            <Card className="bg-white/80 border-teal-100">
              <CardContent className="p-8 text-center">
                <CheckCircle2 className="h-12 w-12 text-teal-500 mx-auto mb-4" />
                <h3 className="font-semibold text-teal-900 mb-2">
                  {t('suggested.allCaughtUp') || "You're all caught up!"}
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  {t('suggested.nothingNew') || 'No new suggestions for now. Keep up the great work!'}
                </p>
                <Button
                  onClick={() => navigate('/allora-chat')}
                  className="bg-gradient-to-r from-teal-500 to-cyan-500"
                >
                  <MessageCircle className="h-4 w-4 mr-2" />
                  {t('suggested.chatWithAllora') || 'Chat with Allora'}
                </Button>
              </CardContent>
            </Card>
          ) : (
            suggestions.map((suggestion) => {
              const colorClasses = getColorClasses(suggestion.color);
              return (
                <button
                  key={suggestion.id}
                  onClick={() => handleSuggestionAction(suggestion)}
                  className={`w-full bg-white rounded-2xl p-4 shadow-sm border ${colorClasses.split(' ').slice(2).join(' ')} text-left hover:shadow-md hover:-translate-y-0.5 transition-all duration-200`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${colorClasses.split(' ').slice(0, 2).join(' ')} flex items-center justify-center flex-shrink-0`}>
                      {suggestion.icon || <span className="text-lg">{suggestion.emoji}</span>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-lg">{suggestion.emoji}</span>
                        <h3 className="font-medium text-gray-900 text-sm">{suggestion.title}</h3>
                        {suggestion.priority === 'high' && (
                          <span className="px-1.5 py-0.5 bg-red-100 text-red-600 text-xs rounded-full">
                            {t('suggested.important') || 'Important'}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 leading-relaxed">{suggestion.content}</p>
                      {suggestion.actionLabel && (
                        <p className="text-xs font-medium mt-2 flex items-center gap-1" style={{ color: suggestion.color === 'purple' ? '#7c3aed' : undefined }}>
                          {suggestion.actionLabel}
                          <ChevronRight className="h-3 w-3" />
                        </p>
                      )}
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>
      </div>

      {/* Floating Action Button */}
      <div className="fixed bottom-6 right-6">
        <Button
          onClick={() => navigate('/allora-chat')}
          className="w-14 h-14 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 shadow-lg"
        >
          <Sparkles className="h-6 w-6 text-white" />
        </Button>
      </div>
    </div>
  );
};

export default SuggestedFeed;
